/**
 * SQL Injection Protection Service
 * Provides secure RPC function calls with input validation
 */

import { supabase } from './supabase'
import { sanitizeRPCParams, detectSQLInjection, sanitizeString } from '../utils/inputSanitization'
import { captureException } from './errorTracking'
import { detectBreachEvent } from './breachDetection'

/**
 * Secure RPC call wrapper
 * Sanitizes parameters and validates inputs before calling RPC functions
 */
export const secureRPC = async (functionName, params = {}, options = {}) => {
  try {
    // Validate function name
    if (typeof functionName !== 'string' || !functionName.trim()) {
      throw new Error('Invalid RPC function name')
    }

    // Check for SQL injection in function name
    if (detectSQLInjection(functionName)) {
      await logSecurityViolation('sql_injection_attempt', {
        functionName,
        reason: 'SQL injection pattern detected in function name',
      })
      throw new Error('Invalid function name')
    }

    // Sanitize parameters
    const sanitizedParams = sanitizeRPCParams(params)

    // Check for SQL injection in parameters
    for (const key in sanitizedParams) {
      const value = sanitizedParams[key]
      if (typeof value === 'string' && detectSQLInjection(value)) {
        await logSecurityViolation('sql_injection_attempt', {
          functionName,
          parameter: key,
          reason: 'SQL injection pattern detected in parameter',
        })
        throw new Error(`Invalid parameter value for ${key}`)
      }
    }

    // Make RPC call
    const { data, error } = await supabase.rpc(functionName, sanitizedParams)

    if (error) {
      // Log error but don't expose SQL details
      captureException(error, {
        context: 'rpc_call',
        functionName,
        sanitized: true,
      })

      // Check if error might indicate SQL injection attempt
      if (
        error.message?.toLowerCase().includes('syntax') ||
        error.message?.toLowerCase().includes('sql') ||
        error.message?.toLowerCase().includes('query')
      ) {
        await logSecurityViolation('sql_injection_attempt', {
          functionName,
          reason: 'SQL syntax error in RPC response',
        })
      }

      throw new Error('Database operation failed')
    }

    return data
  } catch (error) {
    // Re-throw if it's our custom error
    if (error.message === 'Database operation failed' || error.message.includes('Invalid')) {
      throw error
    }

    // Log unexpected errors
    captureException(error, {
      context: 'secure_rpc',
      functionName,
    })
    throw error
  }
}

/**
 * Log security violation
 */
const logSecurityViolation = async (violationType, details) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    const { data: { session } } = await supabase.auth.getSession()

    // Get organization ID if available
    let organizationId = null
    if (user) {
      const { data: orgMember } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', user.id)
        .single()

      organizationId = orgMember?.organization_id
    }

    // Log to audit logs
    await supabase.from('audit_logs').insert({
      organization_id: organizationId,
      user_id: user?.id,
      action_type: 'security_violation',
      resource_type: 'system',
      details: {
        violation_type: violationType,
        ...details,
      },
      ip_address: await getClientIP(),
      user_agent: navigator.userAgent,
    })

    // If critical, trigger breach detection
    if (violationType === 'sql_injection_attempt') {
      if (organizationId) {
        await detectBreachEvent(
          organizationId,
          'unauthorized_access',
          'high',
          `SQL injection attempt detected: ${details.reason}`,
          [],
          user ? [user.id] : [],
          await getClientIP()
        )
      }
    }
  } catch (error) {
    // Don't throw - logging should not break the app
    console.error('Failed to log security violation:', error)
  }
}

/**
 * Get client IP (approximation)
 */
const getClientIP = async () => {
  try {
    // Note: This may be blocked by ad blockers - that's okay
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 3000) // 3 second timeout
    
    const response = await fetch('https://api.ipify.org?format=json', {
      signal: controller.signal,
    })
    
    clearTimeout(timeoutId)
    
    if (!response.ok) {
      return null
    }
    
    const data = await response.json()
    return data.ip
  } catch (error) {
    // Silently fail - IP fetching is optional
    return null
  }
}

/**
 * Validate RPC function parameters against schema
 */
export const validateRPCParams = (functionName, params, schema) => {
  const errors = []

  for (const key in schema) {
    const fieldSchema = schema[key]
    const value = params[key]

    // Check required fields
    if (fieldSchema.required && (value === undefined || value === null)) {
      errors.push(`Missing required parameter: ${key}`)
      continue
    }

    // Skip validation if value is not provided and not required
    if (value === undefined || value === null) {
      continue
    }

    // Validate type
    if (fieldSchema.type) {
      const actualType = Array.isArray(value) ? 'array' : typeof value
      if (actualType !== fieldSchema.type) {
        errors.push(`Invalid type for ${key}: expected ${fieldSchema.type}, got ${actualType}`)
        continue
      }
    }

    // Validate UUID format
    if (fieldSchema.format === 'uuid' && typeof value === 'string') {
      const uuidRegex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
      if (!uuidRegex.test(value)) {
        errors.push(`Invalid UUID format for ${key}`)
      }
    }

    // Validate email format
    if (fieldSchema.format === 'email' && typeof value === 'string') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(value)) {
        errors.push(`Invalid email format for ${key}`)
      }
    }

    // Validate enum
    if (fieldSchema.enum && !fieldSchema.enum.includes(value)) {
      errors.push(`Invalid value for ${key}: must be one of ${fieldSchema.enum.join(', ')}`)
    }

    // Validate min/max for numbers
    if (fieldSchema.type === 'number') {
      if (fieldSchema.min !== undefined && value < fieldSchema.min) {
        errors.push(`Value for ${key} must be at least ${fieldSchema.min}`)
      }
      if (fieldSchema.max !== undefined && value > fieldSchema.max) {
        errors.push(`Value for ${key} must be at most ${fieldSchema.max}`)
      }
    }

    // Validate string length
    if (fieldSchema.type === 'string') {
      if (fieldSchema.minLength && value.length < fieldSchema.minLength) {
        errors.push(`Value for ${key} must be at least ${fieldSchema.minLength} characters`)
      }
      if (fieldSchema.maxLength && value.length > fieldSchema.maxLength) {
        errors.push(`Value for ${key} must be at most ${fieldSchema.maxLength} characters`)
      }
    }
  }

  if (errors.length > 0) {
    throw new Error(`Validation failed: ${errors.join('; ')}`)
  }

  return true
}

/**
 * RPC function parameter schemas
 * Define expected parameters for each RPC function
 */
export const RPC_SCHEMAS = {
  detect_breach_event: {
    org_id: { type: 'string', format: 'uuid', required: true },
    event_type_param: {
      type: 'string',
      enum: [
        'unauthorized_access',
        'data_exfiltration',
        'suspicious_activity',
        'failed_authentication',
        'privilege_escalation',
        'data_modification',
        'export_anomaly',
      ],
      required: true,
    },
    severity_param: {
      type: 'string',
      enum: ['low', 'medium', 'high', 'critical'],
      required: true,
    },
    description_param: { type: 'string', required: true, maxLength: 1000 },
    affected_resources_param: { type: 'array', required: false },
    affected_users_param: { type: 'array', required: false },
    ip_address_param: { type: 'string', required: false },
  },
  generate_compliance_report: {
    org_id: { type: 'string', format: 'uuid', required: true },
    report_type_param: { type: 'string', enum: ['hipaa', 'gdpr', 'general'], required: true },
    period_start_param: { type: 'string', required: true },
    period_end_param: { type: 'string', required: true },
  },
  verify_gdpr_deletion: {
    org_id: { type: 'string', format: 'uuid', required: true },
    client_id_param: { type: 'string', format: 'uuid', required: true },
    request_id: { type: 'string', format: 'uuid', required: false },
  },
  has_feature_access: {
    org_id: { type: 'string', format: 'uuid', required: true },
    feature_code: { type: 'string', required: true, maxLength: 100 },
  },
}

/**
 * Secure RPC call with schema validation
 */
export const secureRPCWithSchema = async (functionName, params = {}) => {
  const schema = RPC_SCHEMAS[functionName]

  if (!schema) {
    console.warn(`No schema defined for RPC function: ${functionName}`)
    // Still sanitize parameters even without schema
    return secureRPC(functionName, params)
  }

  // Validate against schema
  validateRPCParams(functionName, params, schema)

  // Make secure RPC call
  return secureRPC(functionName, params)
}

export default {
  secureRPC,
  secureRPCWithSchema,
  validateRPCParams,
}

